import {
  CustomEventType,
  useDisclosure,
  useDocumentCustomEventListener,
  type MFAResultEventDetails,
} from '@talos/kyoko';
import { createContext, useCallback, useContext, type ReactNode } from 'react';
import { AuthCodeModal, type AuthCodeModalProps } from '../components/AuthCodeModal';

interface AuthCodeModalContextType {
  openAuthCodeModal: (props?: Partial<Omit<AuthCodeModalProps, 'isOpen' | 'onClose'>>) => void;
  closeAuthCodeModal: () => void;
  isOpen: boolean;
}

const AuthCodeModalContext = createContext<AuthCodeModalContextType | undefined>(undefined);

export const useAuthCodeModal = () => {
  const context = useContext(AuthCodeModalContext);
  if (!context) {
    throw new Error('useAuthCodeModal must be used within an AuthCodeModalProvider');
  }
  return context;
};

interface AuthCodeModalProviderProps {
  children: ReactNode;
}

export const AuthCodeModalProvider = ({ children }: AuthCodeModalProviderProps) => {
  const { open, isOpen, close } = useDisclosure();

  const handleMFARequired = useCallback(() => {
    // also send event indicating that we have consumed the event
    document.dispatchEvent(new CustomEvent(CustomEventType.MFA_REQUIRED_CONSUMED));
    open();
  }, [open]);

  useDocumentCustomEventListener({ eventType: CustomEventType.MFA_REQUIRED, handler: handleMFARequired });

  const handleSuccess = useCallback(() => {
    close();
    // dispatch a custom event to the document window to indicate that 2fa is dismissed.
    document.dispatchEvent(
      new CustomEvent<MFAResultEventDetails>(CustomEventType.MFA_RESULT, { detail: { result: 'success' } })
    );
  }, [close]);

  return (
    <AuthCodeModalContext.Provider value={{ openAuthCodeModal: open, closeAuthCodeModal: close, isOpen }}>
      {children}
      <AuthCodeModal isOpen={isOpen} onClose={close} onSuccess={handleSuccess} />
    </AuthCodeModalContext.Provider>
  );
};
